import { projects } from "./data/projects.js";
import { initCursor, initHamburger, initEmailCopy, setYear } from "./scripts/shared.js";

setYear();
initCursor();
initHamburger();
initEmailCopy();

/* ---------- Render project grid ---------- */
const grid = document.getElementById("projectsGrid");
const VISIBLE = 4;

grid.innerHTML = projects
  .map((p, i) => {
    const cls = ["project-card"];
    if (p.status === "wip") cls.push("wip");
    if (i >= VISIBLE) cls.push("hidden-project");
    const tags = p.tags.map((t) => `<span class="tag not-italic">${t}</span>`).join("");
    return `
      <a href="#/project/${p.id}" class="${cls.join(" ")}" data-reveal>
        <div class="card-head">
          <h3 class="card-title"><span class="not-italic">${p.emoji}</span> ${p.name}</h3>
          <span class="status not-italic ${p.status}">${p.status}</span>
        </div>
        <p class="card-desc">${p.short}</p>
        <div class="tags">${tags}</div>
      </a>`;
  })
  .join("");

/* ---------- Reveal cards on scroll ---------- */
const cards = [...grid.querySelectorAll(".project-card")];
const cardObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add("active");
        cardObserver.unobserve(e.target);
      }
    });
  },
  { threshold: 0.15 }
);
cards.forEach((c) => cardObserver.observe(c));

/* ---------- View All Projects ---------- */
const container = document.getElementById("projectsContainer");
const mask = document.getElementById("projectsMask");
const overlay = document.getElementById("viewAllOverlay");
const viewAllBtn = document.getElementById("viewAllBtn");

const hasHidden = projects.length > VISIBLE;
if (hasHidden) {
  mask.classList.add("masked");
} else {
  overlay.classList.add("hidden");
}

viewAllBtn?.addEventListener("click", () => {
  container.classList.add("expanded");
  mask.classList.remove("masked");
  overlay.classList.add("hidden");
  // reveal newly shown cards
  container.querySelectorAll(".hidden-project").forEach((c) => c.classList.add("active"));
});

/* ---------- Nav scroll-spy ---------- */
const navBtns = [...document.querySelectorAll(".nav-btn[data-target]")];
const sections = navBtns
  .map((b) => document.getElementById(b.dataset.target))
  .filter(Boolean);

const spy = new IntersectionObserver(
  (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        const id = e.target.id;
        navBtns.forEach((b) => b.classList.toggle("active", b.dataset.target === id));
      }
    });
  },
  { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
);
sections.forEach((s) => spy.observe(s));

/* ---------- Experience timeline progress ---------- */
const timeline = document.querySelector(".timeline");
const progress = document.getElementById("timelineProgress");
const dots = [...document.querySelectorAll(".exp-item")];

function updateTimeline() {
  if (!timeline || !progress) return;
  const rect = timeline.getBoundingClientRect();
  const vh = window.innerHeight;
  const start = vh * 0.5;
  const filled = Math.min(Math.max(start - rect.top, 0), rect.height);
  progress.style.height = `${(filled / rect.height) * 100}%`;

  dots.forEach((item) => {
    const r = item.getBoundingClientRect();
    item.classList.toggle("reached", r.top < start);
  });
}
window.addEventListener("scroll", updateTimeline, { passive: true });
window.addEventListener("resize", updateTimeline);
updateTimeline();

/* ---------- Hash router: project detail view ---------- */
const home = document.getElementById("home");
const detail = document.getElementById("detail");
const mobileFooter = document.querySelector(".mobile-footer");

const ICONS = {
  github:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 .5A11.5 11.5 0 0 0 .5 12a11.5 11.5 0 0 0 7.86 10.92c.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.54-3.88-1.54-.53-1.34-1.3-1.7-1.3-1.7-1.06-.72.08-.71.08-.71 1.17.08 1.79 1.2 1.79 1.2 1.04 1.79 2.73 1.27 3.4.97.1-.75.4-1.27.73-1.56-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.8 1.19 1.83 1.19 3.09 0 4.42-2.69 5.39-5.25 5.68.41.36.78 1.06.78 2.14v3.17c0 .31.21.67.8.56A11.5 11.5 0 0 0 23.5 12 11.5 11.5 0 0 0 12 .5Z"/></svg>',
  external:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6M10 14 21 3"/></svg>',
};

function renderDetail(project) {
  const tags = project.tags.map((t) => `<span class="tag not-italic">${t}</span>`).join("");
  const body = project.body.map((p) => `<p>${p}</p>`).join("");
  const actions = project.links
    .map(
      (l) =>
        `<a class="detail-btn" href="${l.href}" target="_blank" rel="noopener noreferrer">${
          ICONS[l.type] || ""
        }<span>${l.label}</span></a>`
    )
    .join("");
  detail.innerHTML = `
    <div class="detail-head">
      <h1 class="detail-title"><span class="not-italic">${project.emoji}</span> ${project.name}</h1>
      <span class="status not-italic ${project.status}">${project.status}</span>
    </div>
    <div class="detail-tags">${tags}</div>
    <div class="detail-body">${body}</div>
    ${actions ? `<div class="detail-actions">${actions}</div>` : ""}
    <a href="#projects" class="back-link">← Back to all projects</a>`;
}

function router() {
  const m = location.hash.match(/^#\/project\/(.+)$/);
  const project = m && projects.find((p) => p.id === m[1]);
  if (project) {
    renderDetail(project);
    detail.hidden = false;
    home.hidden = true;
    if (mobileFooter) mobileFooter.hidden = true;
    document.title = `${project.name} · Aditya Kapse`;
    window.scrollTo(0, 0);
  } else {
    detail.hidden = true;
    home.hidden = false;
    if (mobileFooter) mobileFooter.hidden = false;
    document.title = "Aditya Kapse";
  }
}

window.addEventListener("hashchange", router);
router();
